import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { Calendar, DollarSign, Users, FolderOpen, Plus, Search, Filter, Building2, BarChart3, Clock, CheckCircle2, AlertCircle, Eye, Edit, Trash2, User, LogOut, Target, CalendarDays, TrendingUp, AlertTriangle, UserPlus, Settings, Timer, X, ChevronUp, ChevronDown, Play, Download, Moon, Sun, Palette, RefreshCw, FileSpreadsheet, PieChart, LineChart, Archive, ArchiveRestore, Receipt } from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "./components/ui/alert-dialog";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Progress } from "./components/ui/progress";
import { Switch } from "./components/ui/switch";
import { toast, Toaster } from "sonner";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Utility Functions
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(Number(amount));
};

const formatNumber = (number, decimals = 2) => {
  if (number === null || number === undefined || isNaN(number)) return '0.00';
  return Number(number).toFixed(decimals);
};

// Theme Context
const ThemeContext = React.createContext();

const useTheme = () => {
  const context = React.useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

const ThemeProvider = ({ children }) => {
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved === 'dark' || (!saved && window.matchMedia('(prefers-color-scheme: dark)').matches);
  });

  useEffect(() => {
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);

  const toggleTheme = () => setIsDark(!isDark);

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Auth Context
const AuthContext = React.createContext();

const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      console.log('Attempting login with:', { email, password: '***' });
      const response = await axios.post(`${API}/auth/login`, { email, password });
      console.log('Login response:', response.data);
      
      const { access_token, user: userData } = response.data;
      
      if (!access_token || !userData) {
        console.error('Missing access_token or user data in response');
        return { success: false, error: 'Invalid server response' };
      }
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      
      console.log('Login successful, token and user set');
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error response:', error.response?.data);
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const register = async (email, password, name, role = 'staff') => {
    try {
      await axios.post(`${API}/auth/register`, { email, password, name, role });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Registration failed' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    login,
    register,
    logout,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Login Component
const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('staff');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (isLogin) {
      const result = await login(email, password);
      if (result.success) {
        // Successfully logged in, navigate to dashboard
        navigate('/');
      } else {
        toast.error(result.error);
      }
    } else {
      const result = await register(email, password, name, role);
      if (result.success) {
        toast.success('Registration successful! Please login.');
        setIsLogin(true);
        setEmail('');
        setPassword('');
        setName('');
      } else {
        toast.error(result.error);
      }
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-md dark:bg-slate-800 dark:border-slate-700">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 p-3 bg-blue-100 rounded-full w-fit">
            <Building2 className="h-8 w-8 text-blue-700" />
          </div>
          <CardTitle className="text-2xl font-bold text-slate-900 dark:text-slate-100">ArchitecTrack</CardTitle>
          <CardDescription className="dark:text-slate-400">Architectural Project Management</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Enter your full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="project_manager">Project Manager</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <Button type="submit" data-testid="login-submit-btn" className="w-full bg-blue-600 hover:bg-blue-700" disabled={loading}>
              {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Sign Up')}
            </Button>
          </form>
          
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              {isLogin ? 'Need an account? Sign up' : 'Already have an account? Sign in'}
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Header Component
const Header = ({ activeTab, setActiveTab }) => {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  
  return (
    <div>
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-8">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <Building2 className="h-6 w-6 text-blue-700 dark:text-blue-300" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">ArchitecTrack</h1>
                <p className="text-sm text-slate-500 dark:text-slate-400">Project Management</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleTheme}
              data-testid="theme-toggle-btn"
              aria-label="Toggle theme"
              className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100"
            >
              {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <div className="flex items-center space-x-2">
              <User className="h-4 w-4 text-slate-400 dark:text-slate-500" />
              <span className="text-sm text-slate-600 dark:text-slate-300 hidden sm:inline">{user?.name}</span>
              <Badge variant="outline" className="text-xs border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300">
                {user?.role?.replace('_', ' ')}
              </Badge>
            </div>
            <Button variant="ghost" size="sm" onClick={logout} className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>
      
      {/* Mobile-First Navigation Tabs */}
      <nav className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-6 py-2">
        <div className="flex space-x-1 overflow-x-auto">
          <button
            onClick={() => setActiveTab('dashboard')}
            data-testid="nav-dashboard"
            className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
              activeTab === 'dashboard' 
                ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300' 
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800'
            }`}
          >
            <BarChart3 className="h-4 w-4" />
            <span>Dashboard</span>
          </button>
          <button
            onClick={() => setActiveTab('timetrack')}
            data-testid="nav-timetrack"
            className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
              activeTab === 'timetrack' 
                ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300' 
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800'
            }`}
          >
            <Timer className="h-4 w-4" />
            <span>Time</span>
          </button>
          <button
            onClick={() => setActiveTab('schedule')}
            data-testid="nav-schedule"
            className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
              activeTab === 'schedule' 
                ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300' 
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800'
            }`}
          >
            <CalendarDays className="h-4 w-4" />
            <span>Project Schedule</span>
          </button>
          <button
            onClick={() => setActiveTab('staff')}
            data-testid="nav-staff"
            className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
              activeTab === 'staff' 
                ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300' 
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800'
            }`}
          >
            <Users className="h-4 w-4" />
            <span>Staff</span>
          </button>
          <button
            onClick={() => setActiveTab('expenses')}
            data-testid="nav-expenses"
            className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
              activeTab === 'expenses' 
                ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300' 
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800'
            }`}
          >
            <Receipt className="h-4 w-4" />
            <span>Expenses</span>
          </button>
          <button
            onClick={() => setActiveTab('utilization')}
            data-testid="nav-utilization"
            className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
              activeTab === 'utilization' 
                ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300' 
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800'
            }`}
          >
            <PieChart className="h-4 w-4" />
            <span>Analytics</span>
          </button>
        </div>
      </nav>
    </div>
  );
};

// Enhanced Dashboard Stats Component with Annual Budgets
const DashboardStats = ({ stats }) => {
  const currentYear = new Date().getFullYear();
  const budgetYears = Object.keys(stats.annual_budgets || {});
  const years = (budgetYears.length ? budgetYears : [String(currentYear)]).sort();
  
  return (
    <div className="space-y-6">
      {/* Project and Team Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-300">Total Projects</p>
                <p className="text-3xl font-bold text-slate-900 dark:text-slate-100">{stats.total_projects}</p>
              </div>
              <FolderOpen className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-300">Active Projects</p>
                <p className="text-3xl font-bold text-slate-900 dark:text-slate-100">{stats.active_projects}</p>
              </div>
              <Clock className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-300">Team Members</p>
                <p className="text-3xl font-bold text-slate-900 dark:text-slate-100">{stats.total_employees}</p>
              </div>
              <Users className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-300">Overdue Tasks</p>
                <p className="text-3xl font-bold text-red-600">{stats.overdue_milestones || 0}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Annual Budget Stats */}
      <div>
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Annual Budget Overview</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {years.map(year => (
            <Card key={year} data-testid={`annual-year-card-${year}`} className={Number(year) === currentYear ? "ring-2 ring-blue-500" : ""}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{year}</h4>
                  {Number(year) === currentYear && (
                    <Badge variant="default" className="bg-blue-100 text-blue-700">Current</Badge>
                  )}
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-600 dark:text-slate-300">Annual Budget</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {formatCurrency(stats.annual_budgets?.[year] || 0)}
                      </p>
                    </div>
                    <DollarSign className="h-6 w-6 text-blue-600" />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-600 dark:text-slate-300">Expenses</p>
                      <p className="text-xl font-semibold text-red-600">
                        {formatCurrency(stats.annual_expenses?.[year] || 0)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-600 dark:text-slate-300">Remaining</p>
                      <p className="text-xl font-semibold text-green-600">
                        {formatCurrency(stats.annual_remaining?.[year] || 0)}
                      </p>
                    </div>
                  </div>
                  
                  {/* Show profits from completed projects */}
                  {stats.annual_profits?.[year] > 0 && (
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-600 dark:text-slate-300">Completed Project Profits</p>
                        <p className="text-xl font-semibold text-blue-600">
                          {formatCurrency(stats.annual_profits?.[year] || 0)}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Budget Progress Bar */}
                  <div className="w-full bg-slate-200 rounded-full h-3">
                    <div 
                      className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                      style={{
                        width: `${Math.min((stats.annual_expenses?.[year] || 0) / (stats.annual_budgets?.[year] || 1) * 100, 100)}%`
                      }}
                    ></div>
                  </div>
                  
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {formatNumber(((stats.annual_expenses?.[year] || 0) / (stats.annual_budgets?.[year] || 1) * 100), 2)}% utilized
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Additional Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-300">Due This Week</p>
                <p className="text-3xl font-bold text-orange-600">{stats.upcoming_milestones || 0}</p>
              </div>
              <Target className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-300">Total Portfolio Value</p>
                <p className="text-3xl font-bold text-indigo-600">
                  {formatCurrency(stats.total_budget || 0)}
                </p>
              </div>
              <BarChart3 className="h-8 w-8 text-indigo-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-300">Total Completed Project Profits</p>
                <p className="text-3xl font-bold text-blue-600">
                  {formatCurrency(stats.total_profits || 0)}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Staff Assignment Component
const StaffAssignmentWidget = ({ projectId, assignedStaff = [], onStaffUpdated }) => {
  const [employees, setEmployees] = useState([]);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(assignedStaff);
  const { token } = useAuth();

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    setSelectedStaff(assignedStaff);
  }, [assignedStaff]);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data);
    } catch (error) {
      toast.error('Failed to load employees');
    }
  };

  const updateProjectStaff = async () => {
    try {
      await axios.put(`${API}/projects/${projectId}`, {
        assigned_staff: selectedStaff
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Project staff updated successfully!');
      setShowAssignDialog(false);
      onStaffUpdated();
    } catch (error) {
      toast.error('Failed to update project staff');
    }
  };

  const toggleStaffAssignment = (employeeId) => {
    if (selectedStaff.includes(employeeId)) {
      setSelectedStaff(selectedStaff.filter(id => id !== employeeId));
    } else {
      setSelectedStaff([...selectedStaff, employeeId]);
    }
  };

  const getAssignedEmployees = () => {
    return employees.filter(emp => assignedStaff.includes(emp.id));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Assigned Staff</Label>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowAssignDialog(true)}
          className="text-xs"
        >
          <Users className="h-3 w-3 mr-1" />
          Manage
        </Button>
      </div>

      <div className="flex flex-wrap gap-1">
        {getAssignedEmployees().map((employee) => (
          <Badge key={employee.id} variant="secondary" className="text-xs">
            {employee.name}
          </Badge>
        ))}
        {assignedStaff.length === 0 && (
          <span className="text-xs text-slate-500">No staff assigned</span>
        )}
      </div>

      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Staff to Project</DialogTitle>
            <DialogDescription>Select team members to work on this project</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 max-h-60 overflow-y-auto">
            {employees.map((employee) => (
              <div key={employee.id} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id={`staff-${employee.id}`}
                  checked={selectedStaff.includes(employee.id)}
                  onChange={() => toggleStaffAssignment(employee.id)}
                  className="rounded"
                />
                <Label htmlFor={`staff-${employee.id}`} className="flex-1 cursor-pointer">
                  <div>
                    <div className="font-medium">{employee.name}</div>
                    <div className="text-xs text-slate-500">{employee.role} - ${employee.hourly_rate}/hr</div>
                  </div>
                </Label>
              </div>
            ))}
          </div>

          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
              Cancel
            </Button>
            <Button onClick={updateProjectStaff} className="bg-blue-600 hover:bg-blue-700">
              Update Assignment
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Time Tracking Component
const TimeTracking = () => {
  const [timeEntries, setTimeEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [selectedProject, setSelectedProject] = useState("all_projects");
  const [formData, setFormData] = useState({
    employee_id: null,
    project_id: null,
    task_description: "",
    hours: "",
    date: new Date().toISOString().split('T')[0]
  });
  const { token, user } = useAuth();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [timeRes, projectsRes, employeesRes] = await Promise.all([
        axios.get(`${API}/time-entries`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/employees`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setTimeEntries(timeRes.data);
      setProjects(projectsRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      toast.error('Failed to load time tracking data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.employee_id || !formData.project_id || !formData.hours || !formData.task_description) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const entryData = {
        ...formData,
        hours: parseFloat(formData.hours),
        date: new Date(formData.date).toISOString()
      };

      await axios.post(`${API}/time-entries`, entryData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Time entry added successfully!');
      setShowAddEntry(false);
      setFormData({
        employee_id: null,
        project_id: null,
        task_description: "",
        hours: "",
        date: new Date().toISOString().split('T')[0]
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to add time entry');
    }
  };

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(emp => emp.id === employeeId);
    return employee ? employee.name : 'Unknown';
  };

  const getProjectName = (projectId) => {
    const project = projects.find(proj => proj.id === projectId);
    return project ? project.name : 'Unknown';
  };

  const getEmployeeCost = (employeeId, hours) => {
    const employee = employees.find(emp => emp.id === employeeId);
    return employee ? (employee.hourly_rate * hours) : 0;
  };

  const editTimeEntry = (entry) => {
    setEditingEntry(entry);
    setFormData({
      employee_id: entry.employee_id,
      project_id: entry.project_id,
      task_description: entry.task_description,
      hours: entry.hours.toString(),
      date: entry.date.split('T')[0]
    });
    setShowAddEntry(true);
  };

  const updateTimeEntry = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.employee_id || !formData.project_id || !formData.hours || !formData.task_description) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const timeEntryData = {
        employee_id: formData.employee_id,
        project_id: formData.project_id,
        task_description: formData.task_description,
        hours: parseFloat(formData.hours),
        date: formData.date
      };

      await axios.put(`${API}/time-entries/${editingEntry.id}`, timeEntryData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Time entry updated successfully!');
      setShowAddEntry(false);
      setEditingEntry(null);
      setFormData({
        employee_id: null,
        project_id: null,
        task_description: "",
        hours: "",
        date: new Date().toISOString().split('T')[0]
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to update time entry');
    }
  };

  const deleteTimeEntry = async (entryId) => {
    if (window.confirm('Are you sure you want to delete this time entry?')) {
      try {
        await axios.delete(`${API}/time-entries/${entryId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        toast.success('Time entry deleted successfully!');
        fetchData();
      } catch (error) {
        toast.error('Failed to delete time entry');
      }
    }
  };

  const cancelEdit = () => {
    setEditingEntry(null);
    setShowAddEntry(false);
    setFormData({
      employee_id: null,
      project_id: null,
      task_description: "",
      hours: "",
      date: new Date().toISOString().split('T')[0]
    });
  };

  const filteredTimeEntries = selectedProject && selectedProject !== "all_projects" ? 
    timeEntries.filter(entry => entry.project_id === selectedProject) : 
    timeEntries;

  const totalHours = filteredTimeEntries.reduce((sum, entry) => sum + entry.hours, 0);
  const totalCost = filteredTimeEntries.reduce((sum, entry) => sum + getEmployeeCost(entry.employee_id, entry.hours), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Time Tracking</h2>
        <Button
          onClick={() => setShowAddEntry(!showAddEntry)}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Log Time
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{formatNumber(totalHours, 2)}</p>
              <p className="text-sm text-slate-600 dark:text-slate-300">Total Hours</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(totalCost)}</p>
              <p className="text-sm text-slate-600 dark:text-slate-300">Total Cost</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{filteredTimeEntries.length}</p>
              <p className="text-sm text-slate-600 dark:text-slate-300">Entries</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Project Filter */}
      {projects.length > 0 && (
        <div className="flex items-center space-x-4">
          <Label>Filter by Project:</Label>
          <Select value={selectedProject} onValueChange={setSelectedProject}>
            <SelectTrigger className="w-64">
              <SelectValue placeholder="All Projects" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all_projects">All Projects</SelectItem>
              {projects.filter(project => project.id && project.id.trim() !== "").map((project) => (
                <SelectItem key={project.id} value={project.id}>
                  {project.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Add Time Entry Form */}
      {showAddEntry && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Log Time Entry</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={editingEntry ? updateTimeEntry : handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="employee_id">Employee *</Label>
                  {employees.length > 0 ? (
                    <Select value={formData.employee_id || undefined} onValueChange={(value) => setFormData({ ...formData, employee_id: value })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select employee" />
                      </SelectTrigger>
                      <SelectContent>
                        {employees.filter(employee => employee.id && employee.id.trim() !== "").map((employee) => (
                          <SelectItem key={employee.id} value={employee.id}>
                            {employee.name} - {employee.role}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="text-sm text-slate-500 p-2 bg-slate-100 rounded">
                      No employees found. Please add employees first in the Staff tab.
                    </div>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="project_id">Project *</Label>
                  {projects.length > 0 ? (
                    <Select value={formData.project_id || undefined} onValueChange={(value) => setFormData({ ...formData, project_id: value })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select project" />
                      </SelectTrigger>
                      <SelectContent>
                        {projects.filter(project => project.id && project.id.trim() !== "").map((project) => (
                          <SelectItem key={project.id} value={project.id}>
                            {project.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="text-sm text-slate-500 p-2 bg-slate-100 rounded">
                      No projects found. Please create projects first in the Dashboard.
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="hours">Hours *</Label>
                  <Input
                    id="hours"
                    type="number"
                    step="0.25"
                    min="0.25"
                    max="4000"
                    value={formData.hours}
                    onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
                    placeholder="e.g. 8.5"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="date">Date *</Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="task_description">Task Description *</Label>
                <Textarea
                  id="task_description"
                  value={formData.task_description}
                  onChange={(e) => setFormData({ ...formData, task_description: e.target.value })}
                  placeholder="Describe the work done..."
                  required
                />
              </div>

              <div className="flex space-x-2">
                <Button 
                  type="submit" 
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={!formData.employee_id || !formData.project_id}
                >
                  {editingEntry ? 'Update Entry' : 'Log Time'}
                </Button>
                <Button type="button" variant="outline" onClick={editingEntry ? cancelEdit : () => setShowAddEntry(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Time Entries List */}
      <div className="space-y-4">
        {filteredTimeEntries.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Timer className="h-12 w-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No time entries found</h3>
              <p className="text-slate-500 mb-4">
                {employees.length === 0 ? 'Add employees first, then start logging time to track project hours' : 
                 projects.length === 0 ? 'Create projects first, then start logging time to track project hours' :
                 'Start logging time to track project hours'}
              </p>
              {employees.length > 0 && projects.length > 0 && (
                <Button
                  onClick={() => setShowAddEntry(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Log First Entry
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          filteredTimeEntries
            .sort((a, b) => new Date(b.date) - new Date(a.date))
            .map((entry) => (
            <Card key={entry.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h4 className="font-semibold text-slate-900">{getEmployeeName(entry.employee_id)}</h4>
                      <Badge variant="outline" className="text-xs">
                        {getProjectName(entry.project_id)}
                      </Badge>
                      <span className="text-sm text-slate-500">
                        {new Date(entry.date).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 mb-2">{entry.task_description}</p>
                  </div>
                  
                  <div className="text-right flex items-center space-x-3">
                    <div>
                      <div className="text-lg font-bold text-blue-600">{entry.hours}h</div>
                      <div className="text-sm text-slate-500">
                        {formatCurrency(getEmployeeCost(entry.employee_id, entry.hours))}
                      </div>
                    </div>
                    
                    {/* Edit and Delete Buttons */}
                    <div className="flex space-x-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => editTimeEntry(entry)}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deleteTimeEntry(entry.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

// Project Details Dialog
const ProjectDetailsDialog = ({ project, open, onOpenChange, onProjectUpdated }) => {
  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState({
    name: project?.name || '',
    description: project?.description || '',
    client_name: project?.client_name || '',
    start_date: project?.start_date?.split('T')[0] || '',
    end_date: project?.end_date?.split('T')[0] || '',
    planned_budget: project?.planned_budget || '',
    status: project?.status || 'planning',
    priority: project?.priority || 'medium',
    assigned_staff: project?.assigned_staff || []
  });
  const { token } = useAuth();

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (project) {
      setFormData({
        name: project.name || '',
        description: project.description || '',
        client_name: project.client_name || '',
        start_date: project.start_date?.split('T')[0] || '',
        end_date: project.end_date?.split('T')[0] || '',
        planned_budget: project.planned_budget || '',
        status: project.status || 'planning',
        priority: project.priority || 'medium',
        assigned_staff: project.assigned_staff || []
      });
    }
  }, [project]);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data);
    } catch (error) {
      toast.error('Failed to load employees');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const projectData = {
        ...formData,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: new Date(formData.end_date).toISOString(),
        planned_budget: parseFloat(formData.planned_budget)
      };

      await axios.put(`${API}/projects/${project.id}`, projectData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Project updated successfully!');
      onOpenChange(false);
      onProjectUpdated();
    } catch (error) {
      toast.error('Failed to update project');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Project Details</DialogTitle>
          <DialogDescription>View and edit project information</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Project Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="client_name">Client Name</Label>
              <Input
                id="client_name"
                value={formData.client_name}
                onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                required
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
            />
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start_date">Start Date</Label>
              <Input
                id="start_date"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end_date">End Date</Label>
              <Input
                id="end_date"
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="planned_budget">Budget (USD)</Label>
              <Input
                id="planned_budget"
                type="number"
                step="0.01"
                value={formData.planned_budget}
                onChange={(e) => setFormData({ ...formData, planned_budget: e.target.value })}
                required
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status || "planning"} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                <SelectTrigger data-testid="edit-project-status-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="planning">Planning</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="on_hold">On Hold</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select value={formData.priority || "medium"} onValueChange={(value) => setFormData({ ...formData, priority: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Staff Assignment */}
          <div className="space-y-2">
            <Label>Assigned Staff</Label>
            <div className="border rounded-lg p-4 space-y-3">
              {employees.map((employee) => (
                <div key={employee.id} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={`assign-${employee.id}`}
                    checked={formData.assigned_staff.includes(employee.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData({
                          ...formData,
                          assigned_staff: [...formData.assigned_staff, employee.id]
                        });
                      } else {
                        setFormData({
                          ...formData,
                          assigned_staff: formData.assigned_staff.filter(id => id !== employee.id)
                        });
                      }
                    }}
                  />
                  <Label htmlFor={`assign-${employee.id}`} className="flex-1 cursor-pointer">
                    <div>
                      <div className="font-medium">{employee.name}</div>
                      <div className="text-xs text-slate-500">{employee.role} - ${employee.hourly_rate}/hr</div>
                    </div>
                  </Label>
                </div>
              ))}
            </div>
          </div>
          
          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" data-testid="update-project-btn" className="bg-blue-600 hover:bg-blue-700">
              Update Project
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Create Project Dialog
const CreateProjectDialog = ({ onProjectCreated }) => {
  const [open, setOpen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    client_name: '',
    start_date: '',
    end_date: '',
    planned_budget: '',
    status: 'planning',
    priority: 'medium',
    assigned_staff: []
  });
  const { token } = useAuth();

  useEffect(() => {
    if (open) {
      fetchEmployees();
    }
  }, [open]);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data);
    } catch (error) {
      toast.error('Failed to load employees');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const projectData = {
        ...formData,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: new Date(formData.end_date).toISOString(),
        planned_budget: parseFloat(formData.planned_budget)
      };

      await axios.post(`${API}/projects`, projectData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Project created successfully!');
      setOpen(false);
      setFormData({
        name: '',
        description: '',
        client_name: '',
        start_date: '',
        end_date: '',
        planned_budget: '',
        status: 'planning',
        priority: 'medium',
        assigned_staff: []
      });
      onProjectCreated();
    } catch (error) {
      toast.error('Failed to create project');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-blue-600 hover:bg-blue-700">
          <Plus className="h-4 w-4 mr-2" />
          New Project
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Create New Project</DialogTitle>
          <DialogDescription>Add a new project to your portfolio</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Project Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="client_name">Client Name</Label>
              <Input
                id="client_name"
                value={formData.client_name}
                onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                required
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
            />
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start_date">Start Date</Label>
              <Input
                id="start_date"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end_date">End Date</Label>
              <Input
                id="end_date"
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="planned_budget">Budget (USD)</Label>
              <Input
                id="planned_budget"
                type="number"
                step="0.01"
                value={formData.planned_budget}
                onChange={(e) => setFormData({ ...formData, planned_budget: e.target.value })}
                required
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="planning">Planning</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="on_hold">On Hold</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Staff Assignment */}
          <div className="space-y-2">
            <Label>Assign Staff</Label>
            <div className="border rounded-lg p-4 space-y-3 max-h-48 overflow-y-auto">
              {employees.map((employee) => (
                <div key={employee.id} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={`new-assign-${employee.id}`}
                    checked={formData.assigned_staff.includes(employee.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData({
                          ...formData,
                          assigned_staff: [...formData.assigned_staff, employee.id]
                        });
                      } else {
                        setFormData({
                          ...formData,
                          assigned_staff: formData.assigned_staff.filter(id => id !== employee.id)
                        });
                      }
                    }}
                  />
                  <Label htmlFor={`new-assign-${employee.id}`} className="flex-1 cursor-pointer">
                    <div>
                      <div className="font-medium">{employee.name}</div>
                      <div className="text-xs text-slate-500">{employee.role} - ${employee.hourly_rate}/hr</div>
                    </div>
                  </Label>
                </div>
              ))}
            </div>
          </div>
          
          <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">
            Create Project
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Staff Management Component
const StaffManagement = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    email: '',
    hourly_rate: '',
    availability: 'available',
    skills: [],
    weekly_capacity: 40
  });
  const { token } = useAuth();

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data);
    } catch (error) {
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const employeeData = {
        ...formData,
        hourly_rate: parseFloat(formData.hourly_rate),
        weekly_capacity: parseFloat(formData.weekly_capacity),
        skills: formData.skills.filter(skill => skill.trim() !== '')
      };

      if (editingEmployee) {
        await axios.put(`${API}/employees/${editingEmployee.id}`, employeeData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Employee updated successfully!');
      } else {
        await axios.post(`${API}/employees`, employeeData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Employee created successfully!');
      }

      setShowCreateForm(false);
      setEditingEmployee(null);
      setFormData({
        name: '',
        role: '',
        email: '',
        hourly_rate: '',
        availability: 'available',
        skills: [],
        weekly_capacity: 40
      });
      fetchEmployees();
    } catch (error) {
      toast.error('Failed to save employee');
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      name: employee.name,
      role: employee.role,
      email: employee.email,
      hourly_rate: employee.hourly_rate,
      availability: employee.availability,
      skills: employee.skills || [],
      weekly_capacity: employee.weekly_capacity || 40
    });
    setShowCreateForm(true);
  };

  const handleDelete = async (employeeId) => {
    if (window.confirm('Are you sure you want to delete this employee?')) {
      try {
        await axios.delete(`${API}/employees/${employeeId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Employee deleted successfully!');
        fetchEmployees();
      } catch (error) {
        toast.error('Failed to delete employee');
      }
    }
  };

  const addSkill = (skill) => {
    if (skill && !formData.skills.includes(skill)) {
      setFormData({ ...formData, skills: [...formData.skills, skill] });
    }
  };

  const removeSkill = (skillToRemove) => {
    setFormData({
      ...formData,
      skills: formData.skills.filter(skill => skill !== skillToRemove)
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-900">Staff Management</h2>
        <Button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Add Employee
        </Button>
      </div>

      {showCreateForm && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {editingEmployee ? 'Edit Employee' : 'Add New Employee'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="role">Role/Position</Label>
                  <Input
                    id="role"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    placeholder="e.g. Senior Architect"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hourly_rate">Hourly Rate ($)</Label>
                  <Input
                    id="hourly_rate"
                    type="number"
                    step="0.01"
                    value={formData.hourly_rate}
                    onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="weekly_capacity">Weekly Hours</Label>
                  <Input
                    id="weekly_capacity"
                    type="number"
                    value={formData.weekly_capacity}
                    onChange={(e) => setFormData({ ...formData, weekly_capacity: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="availability">Availability Status</Label>
                <Select value={formData.availability || "available"} onValueChange={(value) => setFormData({ ...formData, availability: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="busy">Busy</SelectItem>
                    <SelectItem value="vacation">On Vacation</SelectItem>
                    <SelectItem value="sick">Sick Leave</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Skills</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.skills.map((skill, index) => (
                    <Badge key={index} variant="secondary" className="flex items-center gap-1">
                      {skill}
                      <button
                        type="button"
                        onClick={() => removeSkill(skill)}
                        className="ml-1 text-xs hover:text-red-600"
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add a skill"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addSkill(e.target.value);
                        e.target.value = '';
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={(e) => {
                      const input = e.target.parentNode.querySelector('input');
                      addSkill(input.value);
                      input.value = '';
                    }}
                  >
                    Add
                  </Button>
                </div>
              </div>

              <div className="flex space-x-2">
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  {editingEmployee ? 'Update Employee' : 'Create Employee'}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setShowCreateForm(false);
                    setEditingEmployee(null);
                    setFormData({
                      name: '',
                      role: '',
                      email: '',
                      hourly_rate: '',
                      availability: 'available',
                      skills: [],
                      weekly_capacity: 40
                    });
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4">
        {employees.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Users className="h-12 w-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No employees yet</h3>
              <p className="text-slate-500 mb-4">Add your first employee to get started</p>
              <Button
                onClick={() => setShowCreateForm(true)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Add First Employee
              </Button>
            </CardContent>
          </Card>
        ) : (
          employees.map((employee) => (
            <Card key={employee.id}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h4 className="font-semibold text-slate-900">{employee.name}</h4>
                      <Badge variant="outline" className="text-xs">
                        {employee.availability}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-600 mb-2">{employee.role}</p>
                    <p className="text-sm text-slate-500 mb-2">{employee.email}</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-slate-500">Hourly Rate:</span>
                        <span className="ml-2 font-medium">${employee.hourly_rate}/hr</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Weekly Capacity:</span>
                        <span className="ml-2 font-medium">{employee.weekly_capacity}hrs</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Weekly Cost:</span>
                        <span className="ml-2 font-medium">{formatCurrency(employee.hourly_rate * employee.weekly_capacity)}</span>
                      </div>
                    </div>

                    {employee.skills && employee.skills.length > 0 && (
                      <div className="mt-3">
                        <p className="text-sm text-slate-500 mb-1">Skills:</p>
                        <div className="flex flex-wrap gap-1">
                          {employee.skills.map((skill, index) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {skill}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(employee)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(employee.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

// Milestone Management Component
const MilestoneManager = ({ projectId, onClose }) => {
  const [milestones, setMilestones] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    due_date: '',
    priority: 'medium'
  });
  const { token } = useAuth();

  useEffect(() => {
    fetchMilestones();
  }, [projectId]);

  const fetchMilestones = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/milestones`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMilestones(response.data);
    } catch (error) {
      toast.error('Failed to load milestones');
    }
  };

  const createMilestone = async (e) => {
    e.preventDefault();
    try {
      const milestoneData = {
        ...formData,
        project_id: projectId,
        due_date: new Date(formData.due_date).toISOString()
      };

      await axios.post(`${API}/milestones`, milestoneData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Milestone created successfully!');
      setShowCreateForm(false);
      setFormData({ title: '', description: '', due_date: '', priority: 'medium' });
      fetchMilestones();
    } catch (error) {
      toast.error('Failed to create milestone');
    }
  };

  const updateMilestoneStatus = async (milestoneId, status) => {
    try {
      await axios.put(`${API}/milestones/${milestoneId}`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchMilestones();
      toast.success('Milestone status updated!');
    } catch (error) {
      toast.error('Failed to update milestone');
    }
  };

  const deleteMilestone = async (milestoneId) => {
    if (window.confirm('Are you sure you want to delete this milestone?')) {
      try {
        await axios.delete(`${API}/milestones/${milestoneId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchMilestones();
        toast.success('Milestone deleted successfully!');
      } catch (error) {
        toast.error('Failed to delete milestone');
      }
    }
  };

  const startMilestone = async (milestoneId) => {
    try {
      await axios.put(`${API}/milestones/${milestoneId}/start`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Milestone started!');
      fetchMilestones();
    } catch (error) {
      toast.error('Failed to start milestone');
    }
  };

  const completeMilestone = async (milestoneId) => {
    try {
      await axios.put(`${API}/milestones/${milestoneId}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Milestone completed!');
      fetchMilestones();
    } catch (error) {
      toast.error('Failed to complete milestone');
    }
  };

  const reorderMilestones = async (newMilestones) => {
    try {
      const milestone_orders = newMilestones.map((milestone, index) => ({
        id: milestone.id,
        sort_order: index
      }));

      await axios.put(`${API}/milestones/reorder`, {
        milestone_orders
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMilestones(newMilestones);
      toast.success('Milestones reordered!');
    } catch (error) {
      toast.error('Failed to reorder milestones');
      fetchMilestones(); // Revert on error
    }
  };

  const moveMilestoneUp = (index) => {
    if (index === 0) return;
    const newMilestones = [...milestones];
    [newMilestones[index - 1], newMilestones[index]] = [newMilestones[index], newMilestones[index - 1]];
    reorderMilestones(newMilestones);
  };

  const moveMilestoneDown = (index) => {
    if (index === milestones.length - 1) return;
    const newMilestones = [...milestones];
    [newMilestones[index], newMilestones[index + 1]] = [newMilestones[index + 1], newMilestones[index]];
    reorderMilestones(newMilestones);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-gray-100 text-gray-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'low': return 'text-green-600';
      case 'medium': return 'text-yellow-600';
      case 'high': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Project Milestones</h3>
        <div className="flex space-x-2">
          <Button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Milestone
          </Button>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>

      {showCreateForm && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Create New Milestone</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={createMilestone} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="due_date">Due Date</Label>
                  <Input
                    id="due_date"
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select value={formData.priority || "medium"} onValueChange={(value) => setFormData({ ...formData, priority: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex space-x-2">
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  Create Milestone
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowCreateForm(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="space-y-4">
        {milestones.map((milestone, index) => (
          <Card key={milestone.id} className={`${
            milestone.status === 'overdue' ? 'border-red-200 bg-red-50' : ''
          }`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="font-semibold text-slate-900">{milestone.title}</h4>
                    <Badge className={getStatusColor(milestone.status)}>
                      {milestone.status.replace('_', ' ')}
                    </Badge>
                    <span className={`text-sm font-medium ${getPriorityColor(milestone.priority)}`}>
                      {milestone.priority.toUpperCase()}
                    </span>
                    {milestone.status === 'overdue' && (
                      <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300">
                        OVERDUE
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-slate-600 mb-2">{milestone.description}</p>
                  <div className="flex items-center space-x-4 text-sm text-slate-500">
                    <span>Due: {new Date(milestone.due_date).toLocaleDateString()}</span>
                    {milestone.started_date && (
                      <span>Started: {new Date(milestone.started_date).toLocaleDateString()}</span>
                    )}
                    {milestone.completed_date && (
                      <span>Completed: {new Date(milestone.completed_date).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  {/* Reordering Controls */}
                  <div className="flex flex-col space-y-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => moveMilestoneUp(index)}
                      disabled={index === 0}
                      className="h-6 w-6 p-0"
                    >
                      <ChevronUp className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => moveMilestoneDown(index)}
                      disabled={index === milestones.length - 1}
                      className="h-6 w-6 p-0"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  {/* Manual Control Buttons */}
                  <div className="flex space-x-2">
                    {/* Status Selection Dropdown */}
                    <Select
                      value={milestone.status}
                      onValueChange={(value) => updateMilestoneStatus(milestone.id, value)}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="overdue">Overdue</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {/* Quick Action Buttons */}
                    {milestone.status === 'pending' && (
                      <Button
                        size="sm"
                        onClick={() => startMilestone(milestone.id)}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Play className="h-4 w-4 mr-1" />
                        Start
                      </Button>
                    )}
                    {milestone.status === 'in_progress' && (
                      <Button
                        size="sm"
                        onClick={() => completeMilestone(milestone.id)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle2 className="h-4 w-4 mr-1" />
                        Complete
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => deleteMilestone(milestone.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Order indicator */}
              <div className="mt-2 pt-2 border-t border-slate-200">
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span>Milestone #{index + 1}</span>
                  <span>Order: {milestone.sort_order || index}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        
        {milestones.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center">
              <Target className="h-12 w-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No milestones yet</h3>
              <p className="text-slate-500 mb-4">Add milestones to track project progress</p>
              <Button
                onClick={() => setShowCreateForm(true)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add First Milestone
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// Enhanced Project Card Component
const ProjectCard = ({ project, onProjectUpdated, onViewMilestones, onEditProject, employees = [] }) => {
  const [timeEntries, setTimeEntries] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const { token } = useAuth();

  const handleDeleteProject = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/projects/${project.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Cancelled project deleted');
      onProjectUpdated();
    } catch (error) {
      toast.error('Failed to delete project');
    } finally {
      setDeleting(false);
    }
  };

  const handleArchiveProject = async () => {
    try {
      await axios.put(`${API}/projects/${project.id}/archive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Project archived');
      onProjectUpdated();
    } catch (error) {
      toast.error('Failed to archive project');
    }
  };

  const handleUnarchiveProject = async () => {
    try {
      await axios.put(`${API}/projects/${project.id}/unarchive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Project restored');
      onProjectUpdated();
    } catch (error) {
      toast.error('Failed to restore project');
    }
  };

  useEffect(() => {
    fetchProjectTime();
  }, [project.id]);

  const fetchProjectTime = async () => {
    try {
      const response = await axios.get(`${API}/time-entries?project_id=${project.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTimeEntries(response.data);
    } catch (error) {
      console.error('Failed to load project time entries');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'planning': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'on_hold': return 'bg-red-100 text-red-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-gray-200 text-gray-600';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'low': return 'text-green-600';
      case 'medium': return 'text-yellow-600';
      case 'high': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString();
  };

  const getAssignedEmployees = () => {
    return employees.filter(emp => project.assigned_staff?.includes(emp.id));
  };

  const totalHours = timeEntries.reduce((sum, entry) => sum + entry.hours, 0);
  const totalCost = timeEntries.reduce((sum, entry) => {
    const employee = employees.find(emp => emp.id === entry.employee_id);
    return sum + (employee ? employee.hourly_rate * entry.hours : 0);
  }, 0);

  // Calculate correct budget values including labor costs
  const totalExpenses = project.actual_expenses + totalCost;
  const remainingBudget = project.planned_budget - totalExpenses;
  const budgetUsedPercentage = project.planned_budget > 0 ? (totalExpenses / project.planned_budget) * 100 : 0;

  return (
    <Card className="hover:shadow-lg transition-shadow duration-200" data-testid={`project-card-${project.id}`}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg text-slate-900">{project.name}</CardTitle>
            <CardDescription className="text-slate-600">{project.client_name}</CardDescription>
          </div>
          <div className="flex items-center space-x-2">
            <Badge className={getStatusColor(project.status)}>
              {project.status.replace('_', ' ')}
            </Badge>
            {project.archived && (
              <Badge className="bg-slate-200 text-slate-700" data-testid="archived-badge">Archived</Badge>
            )}
            <span className={`text-xs font-medium ${getPriorityColor(project.priority)}`}>
              {project.priority?.toUpperCase()}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-slate-600 mb-4 line-clamp-2">{project.description}</p>
        
        {/* Assigned Staff */}
        {getAssignedEmployees().length > 0 && (
          <div className="mb-4">
            <Label className="text-xs text-slate-500">Assigned Staff:</Label>
            <div className="flex flex-wrap gap-1 mt-1">
              {getAssignedEmployees().map((employee) => (
                <Badge key={employee.id} variant="secondary" className="text-xs">
                  {employee.name}
                </Badge>
              ))}
            </div>
          </div>
        )}
        
        <div className="space-y-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-slate-500">Duration:</span>
            <span className="text-slate-900">{formatDate(project.start_date)} - {formatDate(project.end_date)}</span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-slate-500">Budget:</span>
            <span className="text-slate-900 dark:text-slate-100">{formatCurrency(project.planned_budget)}</span>
          </div>

          {/* Time Tracking Info */}
          {totalHours > 0 && (
            <>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Hours Logged:</span>
                <span className="text-blue-600 font-medium">{formatNumber(totalHours, 2)}h</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Labor Cost:</span>
                <span className="text-blue-600 font-medium">{formatCurrency(totalCost)}</span>
              </div>
            </>
          )}
          
          <div className="flex items-center justify-between">
            <span className="text-slate-500">Total Expenses:</span>
            <span className="text-slate-700 font-medium">{formatCurrency(totalExpenses)}</span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-slate-500">Used:</span>
            <span className="text-slate-700">{formatNumber(budgetUsedPercentage, 2)}%</span>
          </div>
          
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
              style={{ width: `${Math.min(budgetUsedPercentage, 100)}%` }}
            />
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-slate-500">Remaining:</span>
            <span className={`font-medium ${remainingBudget >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(remainingBudget)}
            </span>
          </div>
        </div>
        
        <div className="flex items-center space-x-2 mt-4">
          <Button variant="outline" size="sm" onClick={() => onEditProject(project)}>
            <Eye className="h-4 w-4 mr-1" />
            View
          </Button>
          <Button variant="outline" size="sm" onClick={() => onEditProject(project)}>
            <Edit className="h-4 w-4 mr-1" />
            Edit
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => onViewMilestones(project.id)}
          >
            <Target className="h-4 w-4 mr-1" />
            Milestones
          </Button>
          {project.status === 'cancelled' && !project.archived && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleArchiveProject}
              className="text-slate-600 hover:text-slate-900"
              data-testid="archive-project-btn"
            >
              <Archive className="h-4 w-4 mr-1" />
              Archive
            </Button>
          )}
          {project.archived && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleUnarchiveProject}
              className="text-blue-600 hover:text-blue-700 border-blue-200"
              data-testid="restore-project-btn"
            >
              <ArchiveRestore className="h-4 w-4 mr-1" />
              Restore
            </Button>
          )}
          {(project.status === 'cancelled' || project.archived) && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={deleting}
                  className="text-red-600 hover:text-red-700 border-red-200 dark:border-red-900"
                  data-testid="delete-project-btn"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="delete-project-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete cancelled project?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This permanently deletes &ldquo;{project.name}&rdquo; along with all its milestones, time entries and budget records. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="delete-project-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDeleteProject}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="delete-project-confirm"
                  >
                    Delete Project
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Project Schedule Component with Timeline Bars
const ProjectSchedule = () => {
  const [scheduleData, setScheduleData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [timelineView, setTimelineView] = useState('months'); // months, weeks, days
  const { token } = useAuth();

  useEffect(() => {
    fetchScheduleData();
  }, []);

  const fetchScheduleData = async () => {
    try {
      const response = await axios.get(`${API}/project-schedule`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScheduleData(response.data.projects);
    } catch (error) {
      toast.error('Failed to load project schedule');
    } finally {
      setLoading(false);
    }
  };

  const moveMilestone = async (milestoneId, newDate) => {
    try {
      await axios.put(`${API}/milestones/${milestoneId}/move`, {
        due_date: newDate.toISOString()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Milestone moved successfully!');
      fetchScheduleData();
    } catch (error) {
      toast.error('Failed to move milestone');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-600';
      case 'in_progress': return 'bg-blue-600';
      case 'on_hold': return 'bg-amber-600';
      case 'planning': return 'bg-gray-600';
      default: return 'bg-gray-500';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-600';
      case 'medium': return 'bg-orange-600';
      case 'low': return 'bg-green-700';
      default: return 'bg-gray-500';
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const calculateTimelinePosition = (date, projectStart, projectEnd) => {
    const totalDuration = new Date(projectEnd) - new Date(projectStart);
    const position = new Date(date) - new Date(projectStart);
    return Math.max(0, Math.min(100, (position / totalDuration) * 100));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-900">Project Schedule</h2>
        <div className="flex items-center space-x-4">
          <Select value={timelineView} onValueChange={setTimelineView}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="months">Months</SelectItem>
              <SelectItem value="weeks">Weeks</SelectItem>
              <SelectItem value="days">Days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Project Timeline */}
      <div className="space-y-6">
        {scheduleData.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <CalendarDays className="h-12 w-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No Projects Found</h3>
              <p className="text-slate-500">Create your first project to see the schedule</p>
            </CardContent>
          </Card>
        ) : (
          scheduleData.map((project) => (
            <Card key={project.id} className="overflow-hidden">
              <CardContent className="p-6">
                <div className="space-y-4">
                  {/* Project Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-slate-900">{project.name}</h3>
                        <Badge variant="outline" className={getPriorityColor(project.priority) + " text-white"}>
                          {project.priority}
                        </Badge>
                        <Badge variant="outline">
                          {project.status.replace('_', ' ')}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600 mb-2">{project.description}</p>
                      <div className="flex items-center space-x-4 text-xs text-slate-500">
                        <span>Client: {project.client_name}</span>
                        <span>Budget: {formatCurrency(project.planned_budget)}</span>
                        <span>Staff: {project.staff_count} members</span>
                        <span>{formatDate(project.start_date)} - {formatDate(project.end_date)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Project Timeline Bar */}
                  <div className="relative">
                    <div className="bg-slate-200 rounded-lg h-8 relative overflow-hidden">
                      {/* Base Progress Bar */}
                      <div 
                        className={`${getStatusColor(project.status)} h-full transition-all duration-300 flex items-center px-3`}
                        style={{ width: `${project.completion_percentage}%` }}
                      >
                        <span className="text-white text-xs font-medium" data-testid={`schedule-completion-${project.id}`}>
                          {formatNumber(project.completion_percentage, 2)}% Complete
                        </span>
                      </div>
                      
                      {/* Milestone Completion Segments */}
                      {project.milestones.map((milestone, index) => {
                        const position = calculateTimelinePosition(
                          milestone.due_date,
                          project.start_date,
                          project.end_date
                        );
                        
                        // Create completion segments behind milestones
                        if (milestone.status === 'completed') {
                          const segmentWidth = 4; // Width of completed segment
                          return (
                            <div
                              key={`segment-${milestone.id}`}
                              className="absolute top-0 h-full bg-blue-400 opacity-70"
                              style={{ 
                                left: `${Math.max(0, position - segmentWidth/2)}%`, 
                                width: `${segmentWidth}%`,
                                zIndex: 1
                              }}
                            ></div>
                          );
                        }
                        return null;
                      })}
                      
                      {/* Milestones on Timeline */}
                      {project.milestones.map((milestone) => {
                        const position = calculateTimelinePosition(
                          milestone.due_date,
                          project.start_date,
                          project.end_date
                        );
                        
                        return (
                          <div
                            key={milestone.id}
                            className="absolute top-0 h-full flex items-center cursor-pointer group z-10"
                            style={{ left: `${position}%` }}
                            title={`${milestone.title} - ${formatDate(milestone.due_date)} (${milestone.status})`}
                          >
                            <div className={`w-3 h-3 rounded-full border-2 border-white shadow-md ${
                              milestone.status === 'completed' ? 'bg-green-600 animate-pulse' :
                              milestone.status === 'overdue' ? 'bg-red-600' :
                              milestone.status === 'in_progress' ? 'bg-blue-600' : 'bg-gray-400'
                            } group-hover:scale-150 transition-all duration-200`}>
                              {milestone.status === 'completed' && (
                                <CheckCircle2 className="w-2 h-2 text-white absolute top-0.5 left-0.5" />
                              )}
                            </div>
                            
                            {/* Milestone tooltip on hover */}
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-slate-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-20">
                              {milestone.title}
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-900"></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Budget Progress Bar */}
                    <div className="mt-2 bg-slate-200 rounded-lg h-3 relative overflow-hidden">
                      <div 
                        className="bg-green-600 h-full transition-all duration-300"
                        style={{ width: `${project.remaining_budget_percentage}%` }}
                        title={`Remaining Budget: ${formatCurrency(project.remaining_budget)}`}
                      ></div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-xs text-slate-800 dark:text-slate-100 font-semibold">
                          {formatCurrency(project.remaining_budget)} remaining
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Milestones List */}
                  {project.milestones.length > 0 && (
                    <div className="border-t pt-4">
                      <h4 className="text-sm font-medium text-slate-700 mb-3">Milestones</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {project.milestones.map((milestone) => (
                          <div key={milestone.id} className="bg-slate-50 rounded-lg p-3">
                            <div className="flex items-center justify-between mb-2">
                              <h5 className="text-sm font-medium text-slate-900">{milestone.title}</h5>
                              <Badge 
                                variant="outline" 
                                className={`text-xs ${
                                  milestone.status === 'completed' ? 'bg-green-100 text-green-700' :
                                  milestone.status === 'overdue' ? 'bg-red-100 text-red-700' :
                                  milestone.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}
                              >
                                {milestone.status}
                              </Badge>
                            </div>
                            <div className="flex items-center justify-between text-xs text-slate-500">
                              <span>{formatDate(milestone.due_date)}</span>
                              <span>{milestone.completion_percentage}%</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

// Enhanced Reports Component with Milestone Tracking
const StaffUtilization = () => {
  const [utilizationData, setUtilizationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [reportType, setReportType] = useState('utilization'); // 'utilization' or 'milestones'
  const [milestones, setMilestones] = useState([]);
  const { token } = useAuth();

  useEffect(() => {
    if (reportType === 'utilization') {
      fetchUtilizationData();
    } else {
      fetchMilestones();
    }
  }, [dateRange, reportType]);

  const fetchUtilizationData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/reports/utilization`, {
        params: {
          start_date: dateRange.start,
          end_date: dateRange.end
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      setUtilizationData(response.data);
    } catch (error) {
      toast.error('Failed to load utilization data');
    } finally {
      setLoading(false);
    }
  };

  const fetchMilestones = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/milestones`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Get project names for each milestone
      const projectResponse = await axios.get(`${API}/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const projects = projectResponse.data;
      
      const milestonesWithProjects = response.data.map(milestone => {
        const project = projects.find(p => p.id === milestone.project_id);
        return {
          ...milestone,
          project_name: project ? project.name : 'Unknown Project',
          project_client: project ? project.client_name : 'Unknown Client'
        };
      });
      
      setMilestones(milestonesWithProjects);
    } catch (error) {
      toast.error('Failed to load milestones');
    } finally {
      setLoading(false);
    }
  };

  const updateMilestoneStatus = async (milestoneId, newStatus) => {
    try {
      await axios.put(`${API}/milestones/${milestoneId}`, {
        status: newStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Milestone status updated!');
      fetchMilestones();
    } catch (error) {
      toast.error('Failed to update milestone status');
    }
  };

  const updateMilestoneCompletion = async (milestoneId, completionPercentage) => {
    try {
      await axios.put(`${API}/milestones/${milestoneId}`, {
        completion_percentage: completionPercentage,
        status: completionPercentage >= 100 ? 'completed' : 'in_progress'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Milestone completion updated!');
      fetchMilestones();
    } catch (error) {
      toast.error('Failed to update milestone completion');
    }
  };

  const getUtilizationColor = (percentage) => {
    if (percentage >= 90) return 'text-red-600';
    if (percentage >= 70) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getUtilizationBg = (percentage) => {
    if (percentage >= 90) return 'bg-red-100';
    if (percentage >= 70) return 'bg-yellow-100';
    return 'bg-green-100';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-900">Reports & Tracking</h2>
        <div className="flex items-center space-x-4">
          {reportType === 'utilization' && (
            <>
              <div className="flex items-center space-x-2">
                <Label htmlFor="start_date">From:</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                  className="w-auto"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Label htmlFor="end_date">To:</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                  className="w-auto"
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Report Type Tabs */}
      <div className="flex items-center space-x-4 border-b border-slate-200">
        <button
          onClick={() => setReportType('utilization')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            reportType === 'utilization'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-slate-600 hover:text-slate-900'
          }`}
        >
          Staff Utilization
        </button>
        <button
          onClick={() => setReportType('milestones')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            reportType === 'milestones'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-slate-600 hover:text-slate-900'
          }`}
        >
          Milestone Tracking
        </button>
      </div>

      {/* Staff Utilization Report */}
      {reportType === 'utilization' && (
        <>
          {utilizationData?.utilization_data?.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Users className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No staff data found</h3>
                <p className="text-slate-500">Add employees to see utilization reports</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {utilizationData?.utilization_data?.map((data) => (
                <Card key={data.employee.id}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h4 className="font-semibold text-slate-900">{data.employee.name}</h4>
                        <p className="text-sm text-slate-600">{data.employee.role}</p>
                        <p className="text-sm text-slate-500">{data.employee.email}</p>
                      </div>
                      <div className="text-right">
                        <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getUtilizationBg(data.utilization_percentage || 0)}`}>
                          <span className={getUtilizationColor(data.utilization_percentage || 0)}>
                            {formatNumber(data.utilization_percentage || 0, 2)}% Utilized
                          </span>
                        </div>
                        <p className="text-sm text-slate-500 mt-1">
                          {formatNumber(data.total_hours || 0, 2)}h / {data.employee?.weekly_capacity || 40}h capacity
                        </p>
                      </div>
                    </div>
                    <div className="bg-slate-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${
                          data.utilization_percentage >= 90
                            ? 'bg-red-500'
                            : data.utilization_percentage >= 70
                            ? 'bg-yellow-500'
                            : 'bg-green-500'
                        }`}
                        style={{ width: `${Math.min(data.utilization_percentage || 0, 100)}%` }}
                      ></div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t">
                      <div>
                        <p className="text-sm font-medium text-slate-900">{data.projects_count || 0}</p>
                        <p className="text-xs text-slate-500">Active Projects</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{formatCurrency(data.total_cost || 0)}</p>
                        <p className="text-xs text-slate-500">Total Cost</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">${data.employee?.hourly_rate || 0}/hr</p>
                        <p className="text-xs text-slate-500">Hourly Rate</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* Milestone Tracking Report */}
      {reportType === 'milestones' && (
        <>
          {milestones.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Target className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No milestones found</h3>
                <p className="text-slate-500">Create milestones in your projects to track progress</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {milestones.map((milestone) => {
                const isOverdue = new Date(milestone.due_date) < new Date() && milestone.status !== 'completed';
                const dueDate = new Date(milestone.due_date).toLocaleDateString();
                
                return (
                  <Card key={milestone.id} className={`${isOverdue ? 'border-red-200 bg-red-50' : ''}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h4 className="font-semibold text-slate-900">{milestone.title}</h4>
                            <Badge 
                              variant="outline" 
                              className={`${
                                milestone.status === 'completed' ? 'bg-green-100 text-green-700 border-green-300' :
                                milestone.status === 'in_progress' ? 'bg-blue-100 text-blue-700 border-blue-300' :
                                isOverdue ? 'bg-red-100 text-red-700 border-red-300' :
                                'bg-gray-100 text-gray-700 border-gray-300'
                              }`}
                            >
                              {isOverdue && milestone.status !== 'completed' ? 'Overdue' : milestone.status.replace('_', ' ')}
                            </Badge>
                            <Badge variant="outline" className={`${
                              milestone.priority === 'high' ? 'bg-red-100 text-red-700' :
                              milestone.priority === 'medium' ? 'bg-orange-100 text-orange-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {milestone.priority}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-600 mb-2">{milestone.project_name}</p>
                          <p className="text-xs text-slate-500">Client: {milestone.project_client}</p>
                          <p className="text-xs text-slate-500">Due: {dueDate}</p>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <Select
                            value={milestone.status}
                            onValueChange={(value) => updateMilestoneStatus(milestone.id, value)}
                            disabled={milestone.status === 'completed'}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="in_progress">In Progress</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                              <SelectItem value="on_hold">On Hold</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-slate-700">Progress</span>
                          <span className="text-sm text-slate-500">{milestone.completion_percentage}%</span>
                        </div>
                        <div className="bg-slate-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all duration-300 ${
                              milestone.completion_percentage >= 100 ? 'bg-green-500' :
                              milestone.completion_percentage >= 75 ? 'bg-blue-500' :
                              milestone.completion_percentage >= 50 ? 'bg-yellow-500' :
                              'bg-gray-500'
                            }`}
                            style={{ width: `${milestone.completion_percentage}%` }}
                          ></div>
                        </div>
                        
                        {milestone.status !== 'completed' && (
                          <div className="flex items-center space-x-2 mt-2">
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              value={milestone.completion_percentage}
                              onChange={(e) => {
                                const value = parseInt(e.target.value);
                                if (!isNaN(value)) {
                                  updateMilestoneCompletion(milestone.id, value);
                                }
                              }}
                              className="w-20"
                            />
                            <span className="text-sm text-slate-500">% complete</span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Advanced Analytics and Reports Component
const AdvancedAnalytics = () => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [reportType, setReportType] = useState('overview');
  const [dateRange, setDateRange] = useState('last30days');
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const { token } = useAuth();

  const dateRanges = {
    last7days: { label: 'Last 7 Days', days: 7 },
    last30days: { label: 'Last 30 Days', days: 30 },
    last90days: { label: 'Last 90 Days', days: 90 },
    thisyear: { label: 'This Year', days: 365 },
    custom: { label: 'Custom Range', days: null }
  };

  useEffect(() => {
    fetchAnalyticsData();
  }, [dateRange, reportType]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      
      const [projectsRes, employeesRes, timeEntriesRes, statsRes] = await Promise.all([
        axios.get(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/employees`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/time-entries`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/dashboard/stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      setProjects(projectsRes.data);
      setEmployees(employeesRes.data);
      setTimeEntries(timeEntriesRes.data);
      
      // Calculate analytics data
      const analytics = calculateAnalytics(projectsRes.data, employeesRes.data, timeEntriesRes.data, statsRes.data);
      setAnalyticsData(analytics);
    } catch (error) {
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const calculateAnalytics = (projects, employees, timeEntries, stats) => {
    const now = new Date();
    const dateFilter = dateRanges[dateRange];
    const startDate = dateFilter.days ? new Date(now.getTime() - (dateFilter.days * 24 * 60 * 60 * 1000)) : null;
    
    // Filter time entries by date range for financial/employee analytics
    const filteredTimeEntries = startDate ? 
      timeEntries.filter(entry => new Date(entry.date) >= startDate) : timeEntries;

    // Project Performance Analytics - use ALL time entries for accurate budget calculations
    const projectAnalytics = projects.map(project => {
      // Use ALL time entries for this project, not just filtered ones for accurate budget tracking
      const projectTimeEntries = timeEntries.filter(entry => entry.project_id === project.id);
      const totalHours = projectTimeEntries.reduce((sum, entry) => sum + entry.hours, 0);
      const totalCost = projectTimeEntries.reduce((sum, entry) => {
        const employee = employees.find(emp => emp.id === entry.employee_id);
        return sum + (employee ? entry.hours * employee.hourly_rate : 0);
      }, 0);
      
      // Calculate correct budget utilization including both actual expenses and labor costs
      const totalExpenses = project.actual_expenses + totalCost;
      const budgetUtilization = project.planned_budget > 0 ? (totalExpenses / project.planned_budget) * 100 : 0;
      const remainingBudget = project.planned_budget - totalExpenses;
      
      // Calculate average hourly rate for this project
      const averageHourlyRate = totalHours > 0 ? totalCost / totalHours : 0;
      
      // Calculate hours remaining based on remaining budget and average hourly rate
      const hoursRemaining = averageHourlyRate > 0 ? remainingBudget / averageHourlyRate : 0;
      
      const timeToCompletion = project.status === 'completed' ? 
        (new Date(project.updated_at) - new Date(project.start_date)) / (1000 * 60 * 60 * 24) : null;

      return {
        ...project,
        totalHours,
        totalCost,
        totalExpenses,
        budgetUtilization,
        remainingBudget,
        averageHourlyRate,
        hoursRemaining: Math.max(hoursRemaining, 0), // Don't show negative hours
        timeToCompletion,
        efficiency: totalHours > 0 ? totalCost / totalHours : 0
      };
    });

    // Employee Productivity Analytics
    const employeeAnalytics = employees.map(employee => {
      const employeeTimeEntries = filteredTimeEntries.filter(entry => entry.employee_id === employee.id);
      const totalHours = employeeTimeEntries.reduce((sum, entry) => sum + entry.hours, 0);
      const totalRevenue = totalHours * employee.hourly_rate;
      const projectCount = [...new Set(employeeTimeEntries.map(entry => entry.project_id))].length;
      const avgHoursPerDay = dateFilter.days ? totalHours / dateFilter.days : 0;
      const utilization = (totalHours / (dateFilter.days * 8)) * 100; // Assuming 8-hour workday

      return {
        ...employee,
        totalHours,
        totalRevenue,
        projectCount,
        avgHoursPerDay,
        utilization: Math.min(utilization, 100)
      };
    });

    // Financial Analytics
    const totalRevenue = filteredTimeEntries.reduce((sum, entry) => {
      const employee = employees.find(emp => emp.id === entry.employee_id);
      return sum + (employee ? entry.hours * employee.hourly_rate : 0);
    }, 0);

    // Calculate total expenses across all projects (including actual expenses + labor costs)
    const totalExpensesAllProjects = projectAnalytics.reduce((sum, project) => sum + project.totalExpenses, 0);

    const totalBudget = projects.reduce((sum, project) => sum + project.planned_budget, 0);
    const activeProjects = projects.filter(p => p.status === 'in_progress').length;
    const avgProjectValue = projects.length > 0 ? totalBudget / projects.length : 0;

    return {
      projects: projectAnalytics,
      employees: employeeAnalytics,
      financial: {
        totalRevenue,
        totalExpenses: totalExpensesAllProjects,
        totalBudget,
        activeProjects,
        avgProjectValue,
        profitMargin: totalBudget > 0 ? ((totalBudget - totalExpensesAllProjects) / totalBudget) * 100 : 0
      },
      annualProfits: stats.annual_profits || {2025: 0, 2026: 0, 2027: 0},
      totalProfits: stats.total_profits || 0,
      timeMetrics: {
        totalHours: filteredTimeEntries.reduce((sum, entry) => sum + entry.hours, 0),
        avgHoursPerProject: projects.length > 0 ? filteredTimeEntries.length / projects.length : 0,
        mostActiveEmployee: employeeAnalytics.reduce((max, emp) => emp.totalHours > max.totalHours ? emp : max, employeeAnalytics[0] || {}),
        mostProfitableProject: projectAnalytics.reduce((max, proj) => proj.budgetUtilization > max.budgetUtilization ? proj : max, projectAnalytics[0] || {})
      }
    };
  };

  const exportToExcel = () => {
    if (!analyticsData) return;

    const wb = XLSX.utils.book_new();

    // Project Performance Sheet
    const projectData = analyticsData.projects.map(project => ({
      'Project Name': project.name,
      'Client': project.client_name,
      'Status': project.status,
      'Budget': project.planned_budget,
      'Hours Logged': formatNumber(project.totalHours),
      'Labor Cost': project.totalCost,
      'Total Expenses': project.totalExpenses,
      'Remaining Budget': project.status !== 'completed' ? project.remainingBudget : '',
      'Profit': project.status === 'completed' ? project.remainingBudget : '',
      'Budget Utilization %': formatNumber(project.budgetUtilization),
      'Hours Remaining': project.status === 'completed' ? 'N/A' : formatNumber(project.hoursRemaining),
      'Average Hourly Rate': formatCurrency(project.averageHourlyRate),
      'Efficiency ($/hour)': formatNumber(project.efficiency),
      'Start Date': new Date(project.start_date).toLocaleDateString(),
      'End Date': new Date(project.end_date).toLocaleDateString()
    }));
    const projectSheet = XLSX.utils.json_to_sheet(projectData);
    XLSX.utils.book_append_sheet(wb, projectSheet, 'Project Performance');

    // Employee Productivity Sheet
    const employeeData = analyticsData.employees.map(employee => ({
      'Employee Name': employee.name,
      'Role': employee.role,
      'Hourly Rate': employee.hourly_rate,
      'Total Hours': formatNumber(employee.totalHours),
      'Total Revenue': employee.totalRevenue,
      'Projects Worked': employee.projectCount,
      'Avg Hours/Day': formatNumber(employee.avgHoursPerDay),
      'Utilization %': formatNumber(employee.utilization),
      'Skills': employee.skills?.join(', ') || ''
    }));
    const employeeSheet = XLSX.utils.json_to_sheet(employeeData);
    XLSX.utils.book_append_sheet(wb, employeeSheet, 'Employee Productivity');

    // Financial Summary Sheet
    const financialData = [{
      'Metric': 'Total Revenue',
      'Value': analyticsData.financial.totalRevenue
    }, {
      'Metric': 'Total Budget',
      'Value': analyticsData.financial.totalBudget
    }, {
      'Metric': 'Active Projects',
      'Value': analyticsData.financial.activeProjects
    }, {
      'Metric': 'Average Project Value',
      'Value': analyticsData.financial.avgProjectValue
    }, {
      'Metric': 'Profit Margin %',
      'Value': analyticsData.financial.profitMargin
    }];
    const financialSheet = XLSX.utils.json_to_sheet(financialData);
    XLSX.utils.book_append_sheet(wb, financialSheet, 'Financial Summary');

    // Time Entries Detail Sheet
    const timeData = timeEntries.map(entry => {
      const employee = employees.find(emp => emp.id === entry.employee_id);
      const project = projects.find(proj => proj.id === entry.project_id);
      return {
        'Date': new Date(entry.date).toLocaleDateString(),
        'Employee': employee?.name || 'Unknown',
        'Project': project?.name || 'Unknown',
        'Client': project?.client_name || 'Unknown',
        'Hours': entry.hours,
        'Task Description': entry.task_description,
        'Cost': employee ? entry.hours * employee.hourly_rate : 0
      };
    });
    const timeSheet = XLSX.utils.json_to_sheet(timeData);
    XLSX.utils.book_append_sheet(wb, timeSheet, 'Time Entries');

    const fileName = `ArchitecTrack_Analytics_${dateRanges[dateRange].label.replace(' ', '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success('Analytics report exported successfully!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h2 className="text-2xl font-bold text-slate-900">Advanced Analytics & Reports</h2>
        <div className="flex items-center space-x-3">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(dateRanges).map(([key, range]) => (
                <SelectItem key={key} value={key}>{range.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={exportToExcel}
            className="bg-blue-600 hover:bg-blue-700"
            disabled={!analyticsData}
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Export Excel
          </Button>
        </div>
      </div>

      {/* Report Type Tabs */}
      <Tabs value={reportType} onValueChange={setReportType}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="projects">Projects</TabsTrigger>
          <TabsTrigger value="employees">Employees</TabsTrigger>
          <TabsTrigger value="financial">Financial</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {analyticsData && (
            <>
              {/* Key Metrics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-600">Total Revenue</p>
                        <p className="text-2xl font-bold text-emerald-600">
                          {formatCurrency(analyticsData.financial.totalRevenue)}
                        </p>
                      </div>
                      <DollarSign className="h-8 w-8 text-emerald-600" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-600">Total Hours</p>
                        <p className="text-2xl font-bold text-blue-600">
                          {formatNumber(analyticsData.timeMetrics.totalHours, 2)}
                        </p>
                      </div>
                      <Clock className="h-8 w-8 text-blue-600" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-600">Active Projects</p>
                        <p className="text-2xl font-bold text-purple-600">{analyticsData.financial.activeProjects}</p>
                      </div>
                      <FolderOpen className="h-8 w-8 text-purple-600" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-600">Profit Margin</p>
                        <p className="text-2xl font-bold text-green-600">
                          {formatNumber(analyticsData.financial.profitMargin, 2)}%
                        </p>
                      </div>
                      <TrendingUp className="h-8 w-8 text-green-600" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Top Performers */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Users className="h-5 w-5" />
                      <span>Most Active Employee</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {analyticsData.timeMetrics.mostActiveEmployee?.name ? (
                      <div className="space-y-2">
                        <h3 className="font-semibold text-lg">{analyticsData.timeMetrics.mostActiveEmployee.name}</h3>
                        <p className="text-slate-600">{analyticsData.timeMetrics.mostActiveEmployee.role}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-500">Hours Logged:</span>
                          <span className="font-medium">{formatNumber(analyticsData.timeMetrics.mostActiveEmployee.totalHours, 2)}h</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-500">Revenue Generated:</span>
                          <span className="font-medium">{formatCurrency(analyticsData.timeMetrics.mostActiveEmployee.totalRevenue)}</span>
                        </div>
                      </div>
                    ) : (
                      <p className="text-slate-500">No data available</p>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <BarChart3 className="h-5 w-5" />
                      <span>Most Profitable Project</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {analyticsData.timeMetrics.mostProfitableProject?.name ? (
                      <div className="space-y-2">
                        <h3 className="font-semibold text-lg">{analyticsData.timeMetrics.mostProfitableProject.name}</h3>
                        <p className="text-slate-600">{analyticsData.timeMetrics.mostProfitableProject.client_name}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-500">Budget Utilization:</span>
                          <span className="font-medium">{formatNumber(analyticsData.timeMetrics.mostProfitableProject.budgetUtilization, 2)}%</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-500">Total Cost:</span>
                          <span className="font-medium">{formatCurrency(analyticsData.timeMetrics.mostProfitableProject.totalCost)}</span>
                        </div>
                      </div>
                    ) : (
                      <p className="text-slate-500">No data available</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* Projects Tab */}
        <TabsContent value="projects" className="space-y-4">
          {analyticsData?.projects.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <FolderOpen className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No project data</h3>
                <p className="text-slate-500">Create projects to see analytics data</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {analyticsData?.projects.map((project) => (
                <Card key={project.id}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg text-slate-900">{project.name}</h3>
                        <p className="text-slate-600">{project.client_name}</p>
                        <Badge variant="outline" className={`mt-2 ${
                          project.status === 'completed' ? 'bg-green-100 text-green-700' :
                          project.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {project.status.replace('_', ' ')}
                        </Badge>
                      </div>
                      
                      <div className="text-right">
                        <div className="text-2xl font-bold text-emerald-600">
                          {formatCurrency(project.planned_budget)}
                        </div>
                        <div className="text-sm text-slate-500">Budget</div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <div className="text-slate-500">Hours Logged</div>
                        <div className="font-medium">{formatNumber(project.totalHours, 2)}h</div>
                      </div>
                      <div>
                        <div className="text-slate-500">Total Expenses</div>
                        <div className="font-medium">{formatCurrency(project.totalExpenses)}</div>
                      </div>
                      <div>
                        <div className="text-slate-500">Budget Used</div>
                        <div className="font-medium">{formatNumber(project.budgetUtilization, 2)}%</div>
                      </div>
                      <div>
                        <div className="text-slate-500">Efficiency</div>
                        <div className="font-medium">{formatCurrency(project.efficiency)}/hr</div>
                      </div>
                    </div>
                    
                    {/* Additional Metrics Row */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mt-3">
                      <div>
                        <div className="text-slate-500">Hours Remaining</div>
                        <div className="font-medium text-blue-600">
                          {project.status === 'completed' ? 'N/A' : `${formatNumber(project.hoursRemaining, 2)}h`}
                        </div>
                      </div>
                      <div>
                        <div className="text-slate-500">
                          {project.status === 'completed' ? 'Profit' : 'Remaining Budget'}
                        </div>
                        <div className={`font-medium ${
                          project.status === 'completed' 
                            ? (project.remainingBudget >= 0 ? 'text-emerald-600' : 'text-red-600')
                            : (project.remainingBudget >= 0 ? 'text-green-600' : 'text-red-600')
                        }`}>
                          {formatCurrency(project.remainingBudget)}
                        </div>
                      </div>
                      <div>
                        <div className="text-slate-500">Avg Rate</div>
                        <div className="font-medium">{formatCurrency(project.averageHourlyRate)}/hr</div>
                      </div>
                      <div>
                        <div className="text-slate-500">Status</div>
                        <div className="font-medium capitalize">
                          {project.status.replace('_', ' ')}
                        </div>
                      </div>
                    </div>
                    
                    {/* Budget Alert for Over-Budget Projects */}
                    {project.budgetUtilization > 100 && (
                      <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <AlertTriangle className="h-4 w-4 text-red-600" />
                          <span className="text-sm font-medium text-red-800">Over Budget</span>
                          <span className="text-sm text-red-600">
                            by {formatCurrency(Math.abs(project.remainingBudget))}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {/* Budget Utilization Bar */}
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-slate-700">Budget Utilization</span>
                        <span className="text-sm text-slate-500">{formatNumber(project.budgetUtilization, 2)}%</span>
                      </div>
                      <div className="bg-slate-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${
                            project.budgetUtilization <= 50 ? 'bg-green-500' :
                            project.budgetUtilization <= 80 ? 'bg-yellow-500' :
                            'bg-red-500'
                          }`}
                          style={{ width: `${Math.min(project.budgetUtilization, 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Employees Tab */}
        <TabsContent value="employees" className="space-y-4">
          {analyticsData?.employees.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Users className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No employee data</h3>
                <p className="text-slate-500">Add employees to see productivity analytics</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {analyticsData?.employees.map((employee) => (
                <Card key={employee.id}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg text-slate-900">{employee.name}</h3>
                        <p className="text-slate-600">{employee.role}</p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {employee.skills?.slice(0, 3).map((skill, index) => (
                            <Badge key={index} variant="secondary" className="text-xs">{skill}</Badge>
                          ))}
                          {employee.skills?.length > 3 && (
                            <Badge variant="secondary" className="text-xs">+{employee.skills.length - 3} more</Badge>
                          )}
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="text-2xl font-bold text-emerald-600">
                          {formatCurrency(employee.hourly_rate)}/hr
                        </div>
                        <div className="text-sm text-slate-500">Rate</div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <div className="text-slate-500">Total Hours</div>
                        <div className="font-medium">{formatNumber(employee.totalHours, 2)}h</div>
                      </div>
                      <div>
                        <div className="text-slate-500">Revenue Generated</div>
                        <div className="font-medium">{formatCurrency(employee.totalRevenue)}</div>
                      </div>
                      <div>
                        <div className="text-slate-500">Projects</div>
                        <div className="font-medium">{employee.projectCount}</div>
                      </div>
                      <div>
                        <div className="text-slate-500">Avg Hours/Day</div>
                        <div className="font-medium">{formatNumber(employee.avgHoursPerDay, 2)}h</div>
                      </div>
                    </div>
                    
                    {/* Utilization Bar */}
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-slate-700">Utilization</span>
                        <span className="text-sm text-slate-500">{formatNumber(employee.utilization, 2)}%</span>
                      </div>
                      <div className="bg-slate-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${
                            employee.utilization >= 80 ? 'bg-green-500' :
                            employee.utilization >= 60 ? 'bg-yellow-500' :
                            'bg-red-500'
                          }`}
                          style={{ width: `${Math.min(employee.utilization, 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Financial Tab */}
        <TabsContent value="financial" className="space-y-6">
          {analyticsData && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-600 dark:text-slate-300">Total Portfolio Value</p>
                        <p className="text-2xl font-bold text-slate-900">
                          {formatCurrency(analyticsData.financial.totalBudget)}
                        </p>
                      </div>
                      <BarChart3 className="h-8 w-8 text-indigo-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-600">Total Expenses</p>
                        <p className="text-2xl font-bold text-red-600">
                          {formatCurrency(analyticsData.financial.totalExpenses)}
                        </p>
                      </div>
                      <DollarSign className="h-8 w-8 text-red-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-600">Average Project Value</p>
                        <p className="text-2xl font-bold text-purple-600">
                          {formatCurrency(analyticsData.financial.avgProjectValue)}
                        </p>
                      </div>
                      <Target className="h-8 w-8 text-purple-600" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Financial Health Indicators */}
              <Card>
                <CardHeader>
                  <CardTitle>Financial Health Overview</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Expenses vs Budget Ratio</span>
                    <span className="font-medium">
                      {formatNumber((analyticsData.financial.totalExpenses / analyticsData.financial.totalBudget) * 100, 2)}%
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Profit Margin</span>
                    <span className={`font-medium ${analyticsData.financial.profitMargin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatNumber(analyticsData.financial.profitMargin, 2)}%
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Total Completed Project Profits</span>
                    <span className="font-medium text-emerald-600">
                      {formatCurrency(analyticsData.totalProfits || 0)}
                    </span>
                  </div>
                  
                  {/* Annual Profits Breakdown */}
                  <div className="border-t pt-3 space-y-2">
                    <h4 className="text-sm font-semibold text-slate-700">Annual Profits Breakdown</h4>
                    {Object.keys(analyticsData.annualProfits || {}).sort().map(year => (
                      <div key={year} className="flex items-center justify-between">
                        <span className="text-slate-600 text-sm">{year} Profits</span>
                        <span className={`font-medium text-sm ${
                          (analyticsData.annualProfits?.[year] || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'
                        }`}>
                          {formatCurrency(analyticsData.annualProfits?.[year] || 0)}
                        </span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Active Projects</span>
                    <span className="font-medium">{analyticsData.financial.activeProjects}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Total Billable Hours</span>
                    <span className="font-medium">{formatNumber(analyticsData.timeMetrics.totalHours, 2)}</span>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Expense Tracker Component (standalone company operating expenses)
const EXPENSE_CATEGORIES = ['Licenses', 'Subscriptions', 'Tools', 'Entertainment/Meals', 'Software', 'Hardware', 'Organizations/Memberships', 'Other'];
const PAYMENT_METHODS = ['Credit Card', 'Debit Card', 'Bank Transfer', 'Cash', 'PayPal', 'Check', 'Other'];
const RECURRING_OPTIONS = [{ value: 'one_time', label: 'One-time' }, { value: 'monthly', label: 'Monthly' }, { value: 'yearly', label: 'Yearly' }];

const ExpenseTracker = () => {
  const { token } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [customCategory, setCustomCategory] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const emptyForm = { description: '', category: '', amount: '', date: new Date().toISOString().split('T')[0], vendor: '', payment_method: '', recurring: 'one_time', notes: '' };
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => { fetchExpenses(); }, []);

  const fetchExpenses = async () => {
    try {
      const res = await axios.get(`${API}/expenses`, { headers: { Authorization: `Bearer ${token}` } });
      setExpenses(res.data);
    } catch (e) {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => { setEditing(null); setFormData(emptyForm); setCustomCategory(false); setShowForm(true); };
  const openEdit = (exp) => {
    setEditing(exp);
    setCustomCategory(!EXPENSE_CATEGORIES.includes(exp.category));
    setFormData({
      description: exp.description || '',
      category: exp.category || '',
      amount: exp.amount ?? '',
      date: exp.date ? exp.date.split('T')[0] : new Date().toISOString().split('T')[0],
      vendor: exp.vendor || '',
      payment_method: exp.payment_method || '',
      recurring: exp.recurring || 'one_time',
      notes: exp.notes || ''
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.description || !formData.category || formData.amount === '' || isNaN(parseFloat(formData.amount))) {
      toast.error('Please fill description, category and a valid amount');
      return;
    }
    const payload = {
      description: formData.description,
      category: formData.category,
      amount: parseFloat(formData.amount),
      date: new Date(formData.date).toISOString(),
      vendor: formData.vendor || null,
      payment_method: formData.payment_method || null,
      recurring: formData.recurring || 'one_time',
      notes: formData.notes || null
    };
    try {
      if (editing) {
        await axios.put(`${API}/expenses/${editing.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Expense updated');
      } else {
        await axios.post(`${API}/expenses`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Expense added');
      }
      setShowForm(false);
      fetchExpenses();
    } catch (err) {
      toast.error('Failed to save expense');
    }
  };

  const handleDelete = async () => {
    if (!expenseToDelete) return;
    try {
      await axios.delete(`${API}/expenses/${expenseToDelete.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Expense deleted');
      setExpenseToDelete(null);
      fetchExpenses();
    } catch (err) {
      toast.error('Failed to delete expense');
    }
  };

  const now = new Date();
  const thisMonthTotal = expenses.filter(e => { const d = new Date(e.date); return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear(); }).reduce((s, e) => s + e.amount, 0);
  const thisYearTotal = expenses.filter(e => new Date(e.date).getFullYear() === now.getFullYear()).reduce((s, e) => s + e.amount, 0);
  const allTimeTotal = expenses.reduce((s, e) => s + e.amount, 0);
  const byCategory = expenses.reduce((acc, e) => { acc[e.category] = (acc[e.category] || 0) + e.amount; return acc; }, {});
  const categoryList = Object.entries(byCategory).sort((a, b) => b[1] - a[1]);
  const allCategories = Array.from(new Set([...EXPENSE_CATEGORIES, ...expenses.map(e => e.category)]));
  const filteredExpenses = categoryFilter === 'all' ? expenses : expenses.filter(e => e.category === categoryFilter);
  const recurringLabel = (v) => (RECURRING_OPTIONS.find(o => o.value === v) || {}).label || v;

  const exportExpenses = () => {
    if (expenses.length === 0) { toast.error('No expenses to export'); return; }
    const wb = XLSX.utils.book_new();
    const ledger = expenses.map(e => ({
      'Date': new Date(e.date).toLocaleDateString(),
      'Description': e.description,
      'Category': e.category,
      'Vendor/Payee': e.vendor || '',
      'Payment Method': e.payment_method || '',
      'Recurring': recurringLabel(e.recurring),
      'Amount': e.amount,
      'Notes': e.notes || ''
    }));
    const ledgerSheet = XLSX.utils.json_to_sheet(ledger);
    XLSX.utils.book_append_sheet(wb, ledgerSheet, 'Expenses');
    const summary = categoryList.map(([cat, amt]) => ({ 'Category': cat, 'Total': amt }));
    summary.push({ 'Category': 'ALL CATEGORIES', 'Total': allTimeTotal });
    const summarySheet = XLSX.utils.json_to_sheet(summary);
    XLSX.utils.book_append_sheet(wb, summarySheet, 'Category Summary');
    XLSX.writeFile(wb, `ArchitecTrack_Expenses_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Expenses exported successfully!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="expense-tracker">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Expense Tracker</h2>
          <p className="text-slate-600 dark:text-slate-400">Track licenses, subscriptions, tools, meals, software, hardware & more</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportExpenses} data-testid="export-expenses-btn">
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Export to Excel
          </Button>
          <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="add-expense-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Expense
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-300">This Month</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-slate-100" data-testid="expense-month-total">{formatCurrency(thisMonthTotal)}</p>
              </div>
              <CalendarDays className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-300">This Year</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-slate-100" data-testid="expense-year-total">{formatCurrency(thisYearTotal)}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-indigo-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-300">All-Time Total</p>
                <p className="text-2xl font-bold text-red-600" data-testid="expense-alltime-total">{formatCurrency(allTimeTotal)}</p>
              </div>
              <DollarSign className="h-8 w-8 text-red-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-300">Entries</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{expenses.length}</p>
              </div>
              <Receipt className="h-8 w-8 text-slate-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Category Breakdown */}
      {categoryList.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Spending by Category</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {categoryList.map(([cat, amt]) => (
              <div key={cat} data-testid={`category-row-${cat}`}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="font-medium text-slate-700 dark:text-slate-300">{cat}</span>
                  <span className="font-semibold text-slate-900 dark:text-slate-100">{formatCurrency(amt)}</span>
                </div>
                <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                  <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${allTimeTotal > 0 ? (amt / allTimeTotal) * 100 : 0}%` }}></div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Filter + Ledger */}
      <div className="flex items-center gap-3">
        <Filter className="h-4 w-4 text-slate-500" />
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-full sm:w-64" data-testid="expense-category-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {allCategories.map(c => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      {filteredExpenses.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center">
            <Receipt className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">No expenses yet</h3>
            <p className="text-slate-500 dark:text-slate-400 mb-4">Start tracking your business expenses</p>
            <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Expense
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700 text-left text-slate-500 dark:text-slate-400">
                  <th className="p-4 font-medium">Date</th>
                  <th className="p-4 font-medium">Description</th>
                  <th className="p-4 font-medium">Category</th>
                  <th className="p-4 font-medium">Vendor</th>
                  <th className="p-4 font-medium">Method</th>
                  <th className="p-4 font-medium">Recurring</th>
                  <th className="p-4 font-medium text-right">Amount</th>
                  <th className="p-4 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredExpenses.map(exp => (
                  <tr key={exp.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800" data-testid={`expense-row-${exp.id}`}>
                    <td className="p-4 text-slate-600 dark:text-slate-300 whitespace-nowrap">{new Date(exp.date).toLocaleDateString()}</td>
                    <td className="p-4 text-slate-900 dark:text-slate-100 font-medium">{exp.description}{exp.notes && <span className="block text-xs text-slate-500 dark:text-slate-400">{exp.notes}</span>}</td>
                    <td className="p-4"><Badge variant="secondary">{exp.category}</Badge></td>
                    <td className="p-4 text-slate-600 dark:text-slate-300">{exp.vendor || '—'}</td>
                    <td className="p-4 text-slate-600 dark:text-slate-300">{exp.payment_method || '—'}</td>
                    <td className="p-4 text-slate-600 dark:text-slate-300">{recurringLabel(exp.recurring)}</td>
                    <td className="p-4 text-right font-semibold text-red-600 whitespace-nowrap">{formatCurrency(exp.amount)}</td>
                    <td className="p-4 text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(exp)} data-testid={`edit-expense-${exp.id}`}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setExpenseToDelete(exp)} className="text-red-600 hover:text-red-700" data-testid={`delete-expense-${exp.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="expense-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Expense' : 'Add Expense'}</DialogTitle>
            <DialogDescription>Record a business expense with its category and details.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="exp-desc">Description *</Label>
              <Input id="exp-desc" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="e.g. Adobe Creative Cloud" data-testid="expense-description-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select
                  value={customCategory ? '__custom__' : formData.category}
                  onValueChange={(v) => {
                    if (v === '__custom__') { setCustomCategory(true); setFormData({ ...formData, category: '' }); }
                    else { setCustomCategory(false); setFormData({ ...formData, category: v }); }
                  }}
                >
                  <SelectTrigger data-testid="expense-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {EXPENSE_CATEGORIES.map(c => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                    <SelectItem value="__custom__">+ Custom category…</SelectItem>
                  </SelectContent>
                </Select>
                {customCategory && (
                  <Input value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} placeholder="Enter custom category" data-testid="expense-custom-category-input" />
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="exp-amount">Amount ($) *</Label>
                <Input id="exp-amount" type="number" step="0.01" min="0" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} placeholder="0.00" data-testid="expense-amount-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="exp-date">Date *</Label>
                <Input id="exp-date" type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} data-testid="expense-date-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="exp-vendor">Vendor / Payee</Label>
                <Input id="exp-vendor" value={formData.vendor} onChange={(e) => setFormData({ ...formData, vendor: e.target.value })} placeholder="e.g. Adobe Inc." data-testid="expense-vendor-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Payment Method</Label>
                <Select value={formData.payment_method} onValueChange={(v) => setFormData({ ...formData, payment_method: v })}>
                  <SelectTrigger data-testid="expense-payment-select"><SelectValue placeholder="Select method" /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map(m => (<SelectItem key={m} value={m}>{m}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Recurring</Label>
                <Select value={formData.recurring} onValueChange={(v) => setFormData({ ...formData, recurring: v })}>
                  <SelectTrigger data-testid="expense-recurring-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RECURRING_OPTIONS.map(o => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="exp-notes">Notes</Label>
              <Textarea id="exp-notes" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Optional notes" data-testid="expense-notes-input" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="save-expense-btn">{editing ? 'Update' : 'Add'} Expense</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!expenseToDelete} onOpenChange={(o) => { if (!o) setExpenseToDelete(null); }}>
        <AlertDialogContent data-testid="delete-expense-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete expense?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete &ldquo;{expenseToDelete?.description}&rdquo;. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-expense-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="delete-expense-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Main Dashboard Component
const Dashboard = () => {
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showMilestones, setShowMilestones] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showProjectDetails, setShowProjectDetails] = useState(false);
  const { token } = useAuth();

  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchData();
    }
  }, [activeTab]);

  const fetchData = async () => {
    try {
      const [projectsRes, statsRes, employeesRes] = await Promise.all([
        axios.get(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/dashboard/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/employees`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setProjects(projectsRes.data);
      setStats(statsRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleEditProject = (project) => {
    setSelectedProject(project);
    setShowProjectDetails(true);
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         project.client_name.toLowerCase().includes(searchQuery.toLowerCase());
    if (statusFilter === 'archived') return matchesSearch && project.archived;
    if (project.archived) return false;
    const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading && activeTab === 'dashboard') {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Show milestone manager if selected
  if (showMilestones) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
        <Header activeTab={activeTab} setActiveTab={setActiveTab} />
        <main className="max-w-7xl mx-auto px-6 py-8">
          <MilestoneManager 
            projectId={showMilestones} 
            onClose={() => setShowMilestones(null)} 
          />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header activeTab={activeTab} setActiveTab={setActiveTab} />
      
      <main className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === 'dashboard' && (
          <>
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100 mb-2">Project Dashboard</h2>
              <p className="text-slate-600 dark:text-slate-400">Manage your architectural projects efficiently</p>
            </div>
            
            <DashboardStats stats={stats} />
            
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
                  <Input
                    placeholder="Search projects..."
                    className="pl-10 w-full sm:w-80"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-48" data-testid="dashboard-status-filter">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="planning">Planning</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="on_hold">On Hold</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <CreateProjectDialog onProjectCreated={fetchData} />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProjects.map((project) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  employees={employees}
                  onProjectUpdated={fetchData}
                  onViewMilestones={setShowMilestones}
                  onEditProject={handleEditProject}
                />
              ))}
            </div>
            
            {filteredProjects.length === 0 && (
              <div className="text-center py-12">
                <FolderOpen className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No projects found</h3>
                <p className="text-slate-500 mb-4">
                  {projects.length === 0 ? 'Get started by creating your first project' : 'Try adjusting your search or filters'}
                </p>
                {projects.length === 0 && <CreateProjectDialog onProjectCreated={fetchData} />}
              </div>
            )}

            {/* Project Details Dialog */}
            <ProjectDetailsDialog
              project={selectedProject}
              open={showProjectDetails}
              onOpenChange={setShowProjectDetails}
              onProjectUpdated={fetchData}
            />
          </>
        )}

        {activeTab === 'timetrack' && <TimeTracking />}
        {activeTab === 'schedule' && <ProjectSchedule />}
        {activeTab === 'staff' && <StaffManagement />}
        {activeTab === 'expenses' && <ExpenseTracker />}
        {activeTab === 'utilization' && <AdvancedAnalytics />}
      </main>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" />;
};

// Main App Component
function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <div className="App bg-white dark:bg-slate-900 min-h-screen transition-colors">
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
            <Toaster position="top-right" />
          </div>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;